if (!customElements.get('metal-performance-scroll')) {
  class MetalPerformanceScroll extends HTMLElement {
    constructor() {
      super();

      this.activeIndex = 0;
      this.rafId = null;
      this.layoutRafId = null;
      this.desktopChartAnimationId = null;
      this.mobileChartAnimationId = null;
      this.desktopObserver = null;
      this.mobileObserver = null;
      this.resizeObserver = null;
      this.boundHandleMediaChange = this.handleMediaChange.bind(this);
      this.boundFallbackUpdate = this.requestFallbackUpdate.bind(this);
      this.boundRefreshLayout = this.requestLayoutRefresh.bind(this);
    }

    connectedCallback() {
      this.cacheElements();

      if (!this.desktopInfoCards.length || !this.desktopChartCards.length || !this.mobileCards.length) {
        return;
      }

      if (this.cardCount < 2) {
        this.applyState(this.activeIndex);
        return;
      }

      this.classList.add('is-enhanced');
      this.bindEvents();
      this.setupResizeObserver();
      this.setupObservers();
      this.applyState(this.activeIndex);
    }

    disconnectedCallback() {
      if (this.desktopObserver) {
        this.desktopObserver.disconnect();
      }

      if (this.mobileObserver) {
        this.mobileObserver.disconnect();
      }

      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
      }

      if (this.desktopMedia) {
        if (typeof this.desktopMedia.removeEventListener === 'function') {
          this.desktopMedia.removeEventListener('change', this.boundHandleMediaChange);
        } else if (typeof this.desktopMedia.removeListener === 'function') {
          this.desktopMedia.removeListener(this.boundHandleMediaChange);
        }
      }

      window.removeEventListener('scroll', this.boundFallbackUpdate);
      window.removeEventListener('resize', this.boundFallbackUpdate);
      window.removeEventListener('resize', this.boundRefreshLayout);

      if (this.rafId) {
        window.cancelAnimationFrame(this.rafId);
        this.rafId = null;
      }

      if (this.layoutRafId) {
        window.cancelAnimationFrame(this.layoutRafId);
        this.layoutRafId = null;
      }

      if (this.desktopChartAnimationId) {
        window.cancelAnimationFrame(this.desktopChartAnimationId);
        this.desktopChartAnimationId = null;
      }

      if (this.mobileChartAnimationId) {
        window.cancelAnimationFrame(this.mobileChartAnimationId);
        this.mobileChartAnimationId = null;
      }
    }

    cacheElements() {
      this.desktopInfoCards = Array.from(this.querySelectorAll('[data-card="info"]'));
      this.desktopChartCards = Array.from(this.querySelectorAll('[data-card="chart"]'));
      this.mobileCards = Array.from(this.querySelectorAll('[data-card="mobile-chart"]'));
      this.desktopInfoSurfaces = this.desktopInfoCards.map((card) => card.querySelector('.metal-performance-scroll__card-surface'));
      this.desktopChartSurfaces = this.desktopChartCards.map((card) => card.querySelector('.metal-performance-scroll__card-surface'));
      this.mobileSurfaces = this.mobileCards.map((card) => card.querySelector('.metal-performance-scroll__card-surface'));
      this.desktopLayout = this.querySelector('[data-role="desktop-layout"]');
      this.mobileLayout = this.querySelector('[data-role="mobile-layout"]');
      this.desktopSteps = Array.from(this.querySelectorAll('[data-role="desktop-track"] [data-step]'));
      this.mobileSteps = Array.from(this.querySelectorAll('[data-role="mobile-track"] [data-step]'));
      this.desktopMedia = window.matchMedia('(min-width: 990px)');
      this.desktopChartDisplay = this.desktopChartCards[0] || null;
      this.mobileChartDisplay = this.mobileCards[0] || null;
      this.chartData = this.desktopChartCards.map((card) => this.readChartData(card));
      this.cardCount = Math.min(
        this.desktopInfoCards.length,
        this.chartData.length,
        this.mobileCards.length
      );
    }

    bindEvents() {
      if (typeof this.desktopMedia.addEventListener === 'function') {
        this.desktopMedia.addEventListener('change', this.boundHandleMediaChange);
      } else if (typeof this.desktopMedia.addListener === 'function') {
        this.desktopMedia.addListener(this.boundHandleMediaChange);
      }

      window.addEventListener('resize', this.boundRefreshLayout, { passive: true });
    }

    setupResizeObserver() {
      if (!('ResizeObserver' in window)) {
        return;
      }

      this.resizeObserver = new ResizeObserver(() => {
        this.requestLayoutRefresh();
      });

      [
        ...this.desktopInfoSurfaces,
        ...this.desktopChartSurfaces,
        ...this.mobileSurfaces,
      ].forEach((surface) => {
        if (surface) {
          this.resizeObserver.observe(surface);
        }
      });
    }

    setupObservers() {
      window.addEventListener('scroll', this.boundFallbackUpdate, { passive: true });
      window.addEventListener('resize', this.boundFallbackUpdate, { passive: true });
      this.requestFallbackUpdate();
    }

    isDesktop() {
      return this.desktopMedia.matches;
    }

    handleMediaChange() {
      this.applyState(this.activeIndex);
      this.requestLayoutRefresh();
      if (!('IntersectionObserver' in window)) {
        this.requestFallbackUpdate();
      }
    }

    requestLayoutRefresh() {
      if (this.layoutRafId) {
        return;
      }

      this.layoutRafId = window.requestAnimationFrame(() => {
        this.layoutRafId = null;
        this.updateStageHeights(this.activeIndex);
      });
    }

    requestFallbackUpdate() {
      if (this.rafId) {
        return;
      }

      this.rafId = window.requestAnimationFrame(() => {
        this.rafId = null;
        this.runFallbackUpdate();
      });
    }

    runFallbackUpdate() {
      const isDesktop = this.isDesktop();
      const activeLayout = isDesktop ? this.desktopLayout : this.mobileLayout;
      if (!activeLayout) {
        return;
      }

      const rect = activeLayout.getBoundingClientRect();
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
      const startLine = viewportHeight * 0.82;
      const endLine = (-1 * rect.height) + (viewportHeight * 0.18);
      const travel = Math.max(1, startLine - endLine);
      const progress = Math.min(0.999999, Math.max(0, (startLine - rect.top) / travel));
      const activationStart = isDesktop ? 0.12 : 0.2;
      const activationEnd = isDesktop ? 0.78 : 0.68;
      const effectiveProgress = Math.min(
        0.999999,
        Math.max(0, (progress - activationStart) / (activationEnd - activationStart))
      );
      const segment = 1 / this.cardCount;
      const leadProgress = segment * (isDesktop ? 0.42 : 0.34);
      const nextIndex = Math.min(
        this.cardCount - 1,
        Math.floor((effectiveProgress + leadProgress) * this.cardCount)
      );
      this.setActive(nextIndex);
    }

    setActive(index) {
      if (Number.isNaN(index)) {
        return;
      }

      const normalizedIndex = Math.max(0, Math.min(this.cardCount - 1, index));
      if (normalizedIndex === this.activeIndex) {
        return;
      }

      this.activeIndex = normalizedIndex;
      this.applyState(this.activeIndex);
    }

    measureSurfaceHeight(surfaces, index) {
      const surface = surfaces[index];

      if (!surface) {
        return 0;
      }

      return surface.offsetHeight;
    }

    parsePointList(points) {
      if (!points) {
        return [];
      }

      return points
        .trim()
        .split(/\s+/)
        .map((pair) => {
          const [x, y] = pair.split(',').map(Number);
          return { x, y };
        })
        .filter((point) => Number.isFinite(point.x) && Number.isFinite(point.y));
    }

    formatPointList(points) {
      return points.map((point) => `${point.x.toFixed(1)},${point.y.toFixed(1)}`).join(' ');
    }

    readChartData(card) {
      if (!card) {
        return null;
      }

      const shape = card.querySelector('.metal-performance-scroll__radar-shape');
      const title = card.querySelector('.metal-performance-scroll__chart-title')?.textContent?.trim() || '';
      const summary = card.dataset.summary || '';
      const accentColor = card.dataset.accentColor || '';

      return {
        theme: card.dataset.theme || 'silver',
        title,
        summary,
        accentColor,
        points: this.parsePointList(shape?.getAttribute('points') || ''),
      };
    }

    getChartDisplayParts(card) {
      if (!card) {
        return null;
      }

      return {
        card,
        title: card.querySelector('.metal-performance-scroll__chart-title'),
        summary: card.querySelector('.metal-performance-scroll__chart-summary'),
        svg: card.querySelector('.metal-performance-scroll__radar'),
        shape: card.querySelector('.metal-performance-scroll__radar-shape'),
        nodes: Array.from(card.querySelectorAll('.metal-performance-scroll__radar-node')),
      };
    }

    applyChartState(parts, data) {
      if (!parts || !data) {
        return;
      }

      parts.card.dataset.theme = data.theme;
      parts.card.dataset.accentColor = data.accentColor;

      if (parts.title) {
        parts.title.textContent = data.title;
      }

      if (parts.summary) {
        parts.summary.textContent = data.summary;
        parts.summary.hidden = !data.summary;
      }

      if (data.accentColor) {
        parts.card.style.setProperty('--metal-performance-theme', data.accentColor);
      } else {
        parts.card.style.removeProperty('--metal-performance-theme');
      }

      if (parts.svg) {
        parts.svg.setAttribute('aria-label', `${data.title} metal performance chart`);
      }

      if (parts.shape && data.points.length) {
        parts.shape.setAttribute('points', this.formatPointList(data.points));
      }

      parts.nodes.forEach((node, index) => {
        const point = data.points[index];
        if (!point) {
          return;
        }

        node.setAttribute('cx', point.x.toFixed(1));
        node.setAttribute('cy', point.y.toFixed(1));
      });
    }

    animateChartDisplay(parts, data, animationKey) {
      if (!parts || !data || !data.points.length) {
        return;
      }

      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const currentPoints = this.parsePointList(parts.shape?.getAttribute('points') || '');

      if (
        reducedMotion ||
        !parts.shape ||
        currentPoints.length !== data.points.length ||
        !currentPoints.length
      ) {
        this.applyChartState(parts, data);
        return;
      }

      this.applyChartState({
        ...parts,
        shape: null,
        nodes: [],
      }, data);

      if (animationKey === 'desktop' && this.desktopChartAnimationId) {
        window.cancelAnimationFrame(this.desktopChartAnimationId);
        this.desktopChartAnimationId = null;
      }

      if (animationKey === 'mobile' && this.mobileChartAnimationId) {
        window.cancelAnimationFrame(this.mobileChartAnimationId);
        this.mobileChartAnimationId = null;
      }

      const duration = 420;
      const startedAt = performance.now();
      const easeOutCubic = (value) => 1 - Math.pow(1 - value, 3);

      const step = (now) => {
        const progress = Math.min(1, (now - startedAt) / duration);
        const eased = easeOutCubic(progress);
        const interpolatedPoints = currentPoints.map((point, index) => {
          const targetPoint = data.points[index];
          return {
            x: point.x + ((targetPoint.x - point.x) * eased),
            y: point.y + ((targetPoint.y - point.y) * eased),
          };
        });

        parts.shape.setAttribute('points', this.formatPointList(interpolatedPoints));
        parts.nodes.forEach((node, index) => {
          const point = interpolatedPoints[index];
          if (!point) {
            return;
          }

          node.setAttribute('cx', point.x.toFixed(1));
          node.setAttribute('cy', point.y.toFixed(1));
        });

        if (progress < 1) {
          const nextFrame = window.requestAnimationFrame(step);
          if (animationKey === 'desktop') {
            this.desktopChartAnimationId = nextFrame;
          } else {
            this.mobileChartAnimationId = nextFrame;
          }
          return;
        }

        if (animationKey === 'desktop') {
          this.desktopChartAnimationId = null;
        } else {
          this.mobileChartAnimationId = null;
        }
      };

      const nextFrame = window.requestAnimationFrame(step);
      if (animationKey === 'desktop') {
        this.desktopChartAnimationId = nextFrame;
      } else {
        this.mobileChartAnimationId = nextFrame;
      }
    }

    renderCharts(index) {
      const data = this.chartData[index];
      if (!data) {
        return;
      }

      this.animateChartDisplay(this.getChartDisplayParts(this.desktopChartDisplay), data, 'desktop');
      this.animateChartDisplay(this.getChartDisplayParts(this.mobileChartDisplay), data, 'mobile');
    }

    updateStageHeights(index) {
      const infoHeight = this.measureSurfaceHeight(this.desktopInfoSurfaces, index);
      const chartHeight = this.measureSurfaceHeight(this.desktopChartSurfaces, index);
      const mobileHeight = this.measureSurfaceHeight(this.mobileSurfaces, index);

      if (infoHeight > 0) {
        this.style.setProperty('--metal-performance-info-stage-current', `${infoHeight}px`);
      }

      if (chartHeight > 0) {
        this.style.setProperty('--metal-performance-chart-stage-current', `${chartHeight}px`);
      }

      if (mobileHeight > 0) {
        this.style.setProperty('--metal-performance-mobile-stage-current', `${mobileHeight}px`);
      }
    }

    applyState(index) {
      const syncCards = (cards) => {
        cards.forEach((card, cardIndex) => {
          const isCurrent = cardIndex === index;
          card.classList.toggle('is-current', isCurrent);
          card.classList.toggle('is-prev', cardIndex < index);
          card.classList.toggle('is-next', cardIndex > index);
          card.setAttribute('aria-hidden', isCurrent ? 'false' : 'true');
        });
      };

      syncCards(this.desktopInfoCards);
      this.renderCharts(index);
      this.updateStageHeights(index);
    }
  }

  customElements.define('metal-performance-scroll', MetalPerformanceScroll);
}
